import { brandColors } from '@assets/colors/brandColors';
import BookmarkIcon from '@assets/images/common/icon/bookmark/BookmarkIcon';
import HeartIcon from '@assets/images/common/icon/heart/HeartIcon';
import LockIcon from '@assets/images/common/icon/lock/LockIcon';
import PageNationNextIcon from '@assets/images/common/icon/pagination/next.svg';
import PageNationPrevIcon from '@assets/images/common/icon/pagination/prev.svg';
import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { MediaTag } from '@src/api/types/mediaTag';
import BookmarkLoginPopup from '@src/components/BookmarkLoginPopup';
import FavoriteLoginPopup from '@src/components/FavoriteLoginPopup';
import Tag from '@src/ui/elements/Company/Tag';
import { formatLikesCount } from '@src/utils/stringUtils';
import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

type Article = {
  to: string;
  title: string;
  content: string;
  date: string;
  category: { id: number; name: string; slug: string };
  tags: MediaTag[];
  thumbnail: string;
  isBookmarked?: boolean;
  onBookmarkToggle?: () => void;
  onBookmarkClickWhenLoggedOut?: () => void;
  bookmarkPopupId?: string;
  isLiked?: boolean;
  likesCount?: number;
  onFavoriteToggle?: () => void;
  onFavoriteClickWhenLoggedOut?: () => void;
  favoritePopupId?: string;
  isMemberLimited?: boolean;
};

type Props = {
  articles: Article[];
  visiblePopupId?: string | null;
  popupRef?: React.RefObject<HTMLDivElement>;
  onPopupClose?: () => void;
};

const ArticleSlider: React.FC<Props> = ({ articles, visiblePopupId, popupRef, onPopupClose }) => {
  const splideRef = useRef<Splide>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const prevVisiblePopupIdRef = useRef(visiblePopupId);

  useEffect(() => {
    if (prevVisiblePopupIdRef.current === visiblePopupId) {
      return;
    }
    prevVisiblePopupIdRef.current = visiblePopupId;

    const autoplay = splideRef.current?.splide?.Components?.Autoplay;
    if (!autoplay) {
      return;
    }

    if (visiblePopupId != null) {
      autoplay.pause();
    } else {
      autoplay.play();
    }
  }, [visiblePopupId]);

  const handlePrev = () => {
    splideRef.current?.go('-1');
  };

  const handleNext = () => {
    splideRef.current?.go('+1');
  };

  const handleCategoryClick = (categorySlug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const query = new URLSearchParams(location.search);
    query.set('category_slug', categorySlug.toString());
    query.set('page', '1');
    navigate(`?${query.toString()}`);
  };

  return (
    <div className='relative'>
      <Splide
        ref={splideRef}
        options={{
          arrows: false,
          pagination: false,
          type: 'loop',
          perPage: 1,
          gap: '1rem',
          autoplay: true,
          interval: 3000,
          pauseOnHover: false,
          breakpoints: {
            1024: {
              type: 'slide',
              rewind: true,
            },
            768: {
              type: 'loop',
              rewind: false,
            },
          },
        }}
      >
        {articles.map((article, index) => (
          <SplideSlide key={index}>
            <div className='flex h-full gap-6 transition-opacity hover:opacity-80 lg:flex-col md:relative md:gap-4'>
              <Link
                to={article.to}
                className='w-[67%] lg:w-full lg:max-w-full'
              >
                <img
                  className='aspect-[500/281] size-full object-contain'
                  src={article.thumbnail}
                  alt=''
                  width='500'
                  height='281'
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = PhotoGrayIcon;
                  }}
                  role='img'
                />
              </Link>
              <div className='flex flex-1 flex-col'>
                <div className='mb-[10px] flex items-center justify-between'>
                  <div
                    className='max-w-[70%] cursor-pointer truncate border-b-2 border-primary-500 py-1 text-xs text-primary'
                    onClick={(e) =>
                      article.category?.id && handleCategoryClick(article.category.slug, e)
                    }
                  >
                    {article.category?.name || 'カテゴリなし'}
                  </div>
                  <div className='flex items-center gap-2'>
                    <time
                      className='text-[10px] text-gray-400'
                      dateTime={article.date}
                    >
                      {article.date}
                    </time>
                    <div className='relative md:static'>
                      <button
                        type='button'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          article.onFavoriteToggle?.();
                          article.onFavoriteClickWhenLoggedOut?.();
                        }}
                        className={`mt-[2px] flex items-center gap-1 ${article.onFavoriteToggle || article.onFavoriteClickWhenLoggedOut ? 'hover:opacity-60' : ''}`}
                        aria-label={article.isLiked ? 'いいね解除' : 'いいね'}
                        data-testid='favorite-button'
                      >
                        <HeartIcon
                          fillColor={article.isLiked ? '#DD2B5C' : '#5F6368'}
                          filled={article.isLiked}
                          width={14}
                          height={14}
                        />
                        <span
                          className={`mt-[-2px] text-[10px] ${article.isLiked ? 'text-like' : 'text-gray-400'}`}
                        >
                          {formatLikesCount(article.likesCount ?? 0)}
                        </span>
                      </button>
                      {popupRef && article.favoritePopupId && (
                        <FavoriteLoginPopup
                          isVisible={visiblePopupId === article.favoritePopupId}
                          popupRef={popupRef}
                          className='left-auto right-0 translate-x-0 md:left-1/2 md:right-auto md:top-[55%] md:-translate-x-1/2'
                          onClose={onPopupClose}
                        />
                      )}
                    </div>
                    <div className='relative md:static'>
                      <button
                        type='button'
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (article.onBookmarkToggle) {
                            article.onBookmarkToggle();
                          }
                          if (article.onBookmarkClickWhenLoggedOut) {
                            article.onBookmarkClickWhenLoggedOut();
                          }
                        }}
                        className={`mt-1.5 transition-opacity ${article.onBookmarkToggle || article.onBookmarkClickWhenLoggedOut ? 'hover:opacity-60' : ''}`}
                        aria-label={article.isBookmarked ? 'ブックマーク解除' : 'ブックマーク'}
                        data-testid='bookmark-button'
                      >
                        <BookmarkIcon
                          fillColor={article.isBookmarked ? brandColors[500] : '#6B7280'}
                          filled={article.isBookmarked}
                          width={14}
                          height={14}
                        />
                      </button>
                      {popupRef && article.bookmarkPopupId && (
                        <BookmarkLoginPopup
                          isVisible={visiblePopupId === article.bookmarkPopupId}
                          popupRef={popupRef}
                          className='left-auto right-0 translate-x-0 md:left-1/2 md:right-auto md:top-[55%] md:-translate-x-1/2'
                          onClose={onPopupClose}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <Link to={article.to}>
                  <div className='flex items-start gap-1 md:mb-[3px]'>
                    {article.isMemberLimited && (
                      <LockIcon
                        fillColor='#D4AF37'
                        className='mt-[9px] shrink-0 size-[22px]'
                        role='img'
                        aria-label='会員限定'
                      />
                    )}
                    <h2 className='min-w-0 line-clamp-2 text-xl font-semibold leading-10'>
                      {article.title}
                    </h2>
                  </div>
                </Link>
                <p className='mb-[20px] line-clamp-3 text-sm md:mb-4 md:line-clamp-2 md:leading-7'>
                  {article.content}
                </p>
                <ul className='flex flex-wrap gap-2 pt-1'>
                  {article.tags.map((tag) => (
                    <Tag
                      key={tag.id}
                      label={tag.name}
                      to={`/articles?tag_slug=${tag.slug}`}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </SplideSlide>
        ))}
      </Splide>

      <div className='mt-4 flex justify-center gap-4'>
        <button
          onClick={handlePrev}
          className='flex size-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50'
        >
          <img
            src={PageNationPrevIcon}
            alt=''
            width={6}
            height={10}
          />
        </button>
        <button
          onClick={handleNext}
          className='flex size-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50'
        >
          <img
            src={PageNationNextIcon}
            alt=''
            width={6}
            height={10}
          />
        </button>
      </div>
    </div>
  );
};

export default ArticleSlider;
